import React, { memo, useCallback, useMemo, useState } from 'react';
import {
  Container,
  IconButton,
  makeStyles,
  NavigateBeforeIcon,
  NavigateNextIcon,
  Tab,
  Tabs,
  useMediaQuery,
} from '@rbx/ui';
import { type TSystemBannerProps } from '@rbx/foundation-ui';
import ChartCard, { ChartCardProps } from './ChartCard';
import { ComparisonChipProps } from './ComparisonChip';
import ChartSummary from './ChartSummary';
import { ChartPlaceholderProps } from './ChartPlaceholder';
import useMuiTabsScroller from './useMuiTabsScroller';
import type { ChartCardDragDropOptions, ChartCardResizeOptions } from './ChartCardDragDropContext';
import ChartCardDragAndResizeContainer from './ChartCardDragAndResizeContainer';

type TabSpec<TTabKey> = {
  key: TTabKey;
  summaryValue: string;
  description: string;
  comparisonChipSpec?: ComparisonChipProps;
  abnormalState?: ChartPlaceholderProps;
  tooltip?: string;
  chartBanner?: TSystemBannerProps;
};

type TabbedChartsCardContainerProps<TTabKey> = Omit<
  ChartCardProps,
  'subTitle' | 'abnormalState' | 'chartBanner'
> & {
  chartControl?: React.ReactNode;
  tabSpecs: Array<TabSpec<TTabKey>>;
  activeTabKey: TTabKey;
  onActiveTabChanged: (tabKey: TTabKey) => void;
  dragAndDropOptions?: ChartCardDragDropOptions;
  resizeOptions?: ChartCardResizeOptions;
};

const useStyles = makeStyles()((theme) => ({
  subTitleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0px 4px',
  },
  tabsWrapper: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
  },
  scrollBumper: {
    display: 'flex',
    alignItems: 'center',
    zIndex: theme.zIndex.speedDial,
    position: 'absolute',
    height: '100%',
    top: 0,
  },
  scrollBumperLeft: {
    left: 0,
  },
  scrollBumperRight: {
    right: 0,
  },
  scrollBumperHidden: {
    opacity: 0,
    pointerEvents: 'none',
  },
  scrollButton: {
    opacity: 1,
    alignSelf: 'center',
  },
  chartControlContainer: {
    flex: '0 1 auto',
    marginLeft: '16px',
  },
  tabContainer: {
    paddingTop: '0px',
    paddingBottom: '4px',
    lineHeight: '110%',
  },
}));

const TabbedChartsCardContainer = <TTabKey extends string | number>({
  children,
  titleLabel,
  titleTooltipLabel,
  titleSuffix,
  footerContent,
  downloadAction,
  secondaryAction,
  chartControl,
  tabSpecs,
  activeTabKey,
  onActiveTabChanged,
  dragAndDropOptions,
  resizeOptions,
}: React.PropsWithChildren<TabbedChartsCardContainerProps<TTabKey>>) => {
  const {
    classes: {
      subTitleContainer,
      tabsWrapper,
      scrollBumper,
      scrollBumperLeft,
      scrollBumperRight,
      scrollBumperHidden,
      scrollButton,
      chartControlContainer,
      tabContainer,
    },
    cx,
  } = useStyles();
  const isCompactView = useMediaQuery((theme) => theme.breakpoints.down('Medium'));

  // Scroll bumper (desktop only)
  const { wrapperRef, isStartOfScroll, isEndOfScroll, handleScroll, refreshScrollState } =
    useMuiTabsScroller(!isCompactView);
  const [isHover, setIsHover] = useState(false);

  // --- Tab change handler ---
  const handleValueChange = useCallback(
    (_: React.ChangeEvent<object>, newValue: TTabKey) => {
      onActiveTabChanged(newValue);
    },
    [onActiveTabChanged],
  );

  // Memoize the Tabs element (the expensive part)
  const tabs = useMemo(() => (
    <Tabs
      value={activeTabKey}
      variant='scrollable'
      scrollButtons={false}
      onChange={handleValueChange}
      aria-label='tabs'>
      {tabSpecs.map(
        ({ key, comparisonChipSpec, description, summaryValue, abnormalState, tooltip }) => (
          <Tab
            label={
              <ChartSummary
                summaryValue={summaryValue}
                description={description}
                comparisonChipSpec={comparisonChipSpec}
                abnormalStatus={abnormalState?.status}
                tooltip={tooltip}
              />
            }
            value={key}
            key={key}
            classes={{ root: tabContainer }}
          />
        ),
      )}
    </Tabs>
  ), [activeTabKey, handleValueChange, tabContainer, tabSpecs]);

  const subTitle = (
    <Container disableGutters classes={{ root: subTitleContainer }} maxWidth={false}>
      <div
        ref={wrapperRef}
        className={tabsWrapper}
        onMouseEnter={() => {
          setIsHover(true);
          refreshScrollState();
        }}
        onMouseLeave={() => setIsHover(false)}>
        {tabs}
        {!isCompactView && (
          <React.Fragment>
            <div
              className={cx(scrollBumper, scrollBumperLeft, {
                [scrollBumperHidden]: !isHover || isStartOfScroll,
              })}>
              <IconButton
                classes={{ root: scrollButton }}
                onClick={() => handleScroll('left')}
                color='onMediaLight'
                variant='contained'
                aria-label='scroll left'
                size='small'>
                <NavigateBeforeIcon fontSize='small' />
              </IconButton>
            </div>
            <div
              className={cx(scrollBumper, scrollBumperRight, {
                [scrollBumperHidden]: !isHover || isEndOfScroll,
              })}>
              <IconButton
                classes={{ root: scrollButton }}
                onClick={() => handleScroll('right')}
                color='onMediaLight'
                variant='contained'
                aria-label='scroll right'
                size='small'>
                <NavigateNextIcon fontSize='small' />
              </IconButton>
            </div>
          </React.Fragment>
        )}
      </div>
      {!isCompactView && chartControl && (
        <div className={chartControlContainer}>{chartControl}</div>
      )}
    </Container>
  );

  const abnormalState = useMemo(() => {
    const activeTab = tabSpecs.find((tab) => tab.key === activeTabKey);
    return activeTab?.abnormalState;
  }, [activeTabKey, tabSpecs]);

  const chartBanner = useMemo(() => {
    const activeTab = tabSpecs.find((tab) => tab.key === activeTabKey);
    return activeTab?.chartBanner;
  }, [activeTabKey, tabSpecs]);

  return (
    <ChartCardDragAndResizeContainer
      dragAndDropOptions={dragAndDropOptions}
      resizeOptions={resizeOptions}>
      <ChartCard
        titleLabel={titleLabel}
        titleTooltipLabel={titleTooltipLabel}
        titleSuffix={titleSuffix}
        secondaryAction={secondaryAction}
        downloadAction={downloadAction}
        footerContent={footerContent}
        subTitle={subTitle}
        chartControl={isCompactView ? chartControl : undefined}
        abnormalState={abnormalState}
        chartBanner={chartBanner}>
        {children}
      </ChartCard>
    </ChartCardDragAndResizeContainer>
  );
};

// memo() erases generic type parameters; cast through unknown to restore them
export default memo(TabbedChartsCardContainer) as unknown as typeof TabbedChartsCardContainer;
